import { useState, useEffect, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '../components/ui/dialog';
import { toast } from 'sonner';
import {
  ArrowLeft, Plus, Edit, Trash2, Send, Save, Clock, Calendar,
  Mail, FileText, Play, Pause, CheckCircle, XCircle, History
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function authHeaders() {
  const t = localStorage.getItem('area_auth_token');
  return { 'Content-Type': 'application/json', ...(t ? { Authorization: `Bearer ${t}` } : {}) };
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

export default function AdminScheduledReports() {
  const { user, loading: authLoading, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [reportTypes, setReportTypes] = useState([]);
  const [schedules, setSchedules] = useState([]);
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [editSched, setEditSched] = useState(null);
  const [tab, setTab] = useState('schedules');

  useEffect(() => {
    if (!authLoading && (!user || !isAdmin)) navigate('/login');
  }, [user, authLoading, isAdmin, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    const [tRes, sRes, hRes] = await Promise.all([
      fetch(`${API}/scheduled-reports/report-types`, { headers: authHeaders(), credentials: 'include' }),
      fetch(`${API}/scheduled-reports/schedules`, { headers: authHeaders(), credentials: 'include' }),
      fetch(`${API}/scheduled-reports/history?limit=30`, { headers: authHeaders(), credentials: 'include' }),
    ]);
    if (tRes.ok) { const d = await tRes.json(); setReportTypes(d.report_types || []); }
    if (sRes.ok) { const d = await sRes.json(); setSchedules(d.schedules || []); }
    if (hRes.ok) { const d = await hRes.json(); setHistory(d.history || []); }
    setLoading(false);
  }, []);

  useEffect(() => { if (user && isAdmin) load(); }, [user, isAdmin, load]);

  const blankSchedule = () => ({
    report_type: reportTypes[0]?.id || 'affiliate_performance',
    frequency: 'weekly',
    day_of_week: 1,
    day_of_month: 1,
    recipient_emails: [user?.email || ''],
  });

  const save = async () => {
    const isNew = !editSched.schedule_id;
    const url = isNew ? `${API}/scheduled-reports/schedules` : `${API}/scheduled-reports/schedules/${editSched.schedule_id}`;
    const method = isNew ? 'POST' : 'PUT';
    const emails = editSched.recipient_emails.filter(e => e.trim());
    const res = await fetch(url, { method, headers: authHeaders(), credentials: 'include', body: JSON.stringify({ ...editSched, recipient_emails: emails }) });
    if (res.ok) { toast.success(isNew ? 'Schedule created' : 'Schedule updated'); setShowDialog(false); load(); }
    else toast.error('Failed to save');
  };

  const deleteSched = async (id) => {
    if (!window.confirm('Delete this schedule?')) return;
    await fetch(`${API}/scheduled-reports/schedules/${id}`, { method: 'DELETE', headers: authHeaders(), credentials: 'include' });
    toast.success('Schedule deleted'); load();
  };

  const toggleActive = async (sched) => {
    await fetch(`${API}/scheduled-reports/schedules/${sched.schedule_id}`, {
      method: 'PUT', headers: authHeaders(), credentials: 'include',
      body: JSON.stringify({ is_active: !sched.is_active }),
    });
    toast.success(sched.is_active ? 'Schedule paused' : 'Schedule activated'); load();
  };

  const sendNow = async (id) => {
    toast.info('Generating and sending report...');
    const res = await fetch(`${API}/scheduled-reports/schedules/${id}/send-now`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    if (res.ok) { const d = await res.json(); toast.success(d.sent ? 'Report sent!' : 'Send failed'); load(); }
    else toast.error('Failed to send');
  };

  const processAll = async () => {
    toast.info('Processing due reports...');
    const res = await fetch(`${API}/scheduled-reports/process-due`, { method: 'POST', headers: authHeaders(), credentials: 'include' });
    if (res.ok) { const d = await res.json(); toast.success(`Processed ${d.processed} report(s)`); load(); }
  };

  if (authLoading || loading) return <div className="max-w-5xl mx-auto px-4 py-12"><div className="h-48 bg-gray-50 rounded-xl animate-pulse" /></div>;
  if (!user || !isAdmin) return null;

  return (
    <div className="min-h-screen bg-[#fafbfc]" data-testid="admin-scheduled-reports-page">
      <div className="max-w-5xl mx-auto px-4 py-8">
        <div className="flex items-center gap-3 mb-6">
          <Link to="/admin"><Button variant="ghost" size="sm"><ArrowLeft className="w-4 h-4 mr-1" /> Admin</Button></Link>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-[#0f1f33]">Scheduled Reports</h1>
            <p className="text-sm text-gray-500">Configure automated CSV reports delivered to your inbox.</p>
          </div>
          <Button size="sm" variant="outline" onClick={processAll} data-testid="process-due-btn">
            <Play className="w-4 h-4 mr-1" /> Process Due
          </Button>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 mb-6 bg-gray-100 p-1 rounded-lg w-fit">
          <button onClick={() => setTab('schedules')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'schedules' ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-schedules">
            <Calendar className="w-3.5 h-3.5 inline mr-1" /> Schedules ({schedules.length})
          </button>
          <button onClick={() => setTab('history')} className={`px-4 py-1.5 rounded-md text-sm font-medium transition-colors ${tab === 'history' ? 'bg-white text-[#0f1f33] shadow-sm' : 'text-gray-500 hover:text-gray-700'}`} data-testid="tab-history">
            <History className="w-3.5 h-3.5 inline mr-1" /> History ({history.length})
          </button>
        </div>

        {/* Schedules Tab */}
        {tab === 'schedules' && (
          <div className="space-y-4">
            <div className="flex justify-end">
              <Button size="sm" onClick={() => { setEditSched(blankSchedule()); setShowDialog(true); }} data-testid="add-schedule-btn">
                <Plus className="w-4 h-4 mr-1" /> New Schedule
              </Button>
            </div>

            {schedules.length === 0 ? (
              <Card className="border-gray-100">
                <CardContent className="py-12 text-center text-gray-400">
                  <Clock className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No scheduled reports yet. Create one to get started.</p>
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2">
                {schedules.map(s => (
                  <Card key={s.schedule_id} className="border-gray-100" data-testid={`schedule-row-${s.schedule_id}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center gap-4">
                        <div className={`w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0 ${s.is_active ? 'bg-emerald-50' : 'bg-gray-50'}`}>
                          <FileText className={`w-5 h-5 ${s.is_active ? 'text-emerald-600' : 'text-gray-400'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-semibold text-[#0f1f33]">{s.report_name}</h3>
                            <Badge className={`text-[9px] ${s.is_active ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-500'}`}>
                              {s.is_active ? 'Active' : 'Paused'}
                            </Badge>
                            <Badge className="text-[9px] bg-blue-50 text-blue-700">
                              {s.frequency === 'weekly' ? `Every ${DAYS_OF_WEEK[s.day_of_week || 0]}` : s.frequency === 'monthly' ? `Monthly (${s.day_of_month || 1}st)` : 'Daily'}
                            </Badge>
                          </div>
                          <div className="flex items-center gap-3 mt-1 text-xs text-gray-400">
                            <span className="flex items-center gap-1"><Mail className="w-3 h-3" /> {s.recipient_emails?.join(', ')}</span>
                            {s.last_sent_at && <span>Last: {new Date(s.last_sent_at).toLocaleDateString()}</span>}
                            <span>Sent {s.send_count || 0}x</span>
                            {s.last_status && (
                              <Badge className={`text-[9px] ${s.last_status === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>
                                {s.last_status}
                              </Badge>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-1 flex-shrink-0">
                          <Button variant="ghost" size="sm" className="h-7 px-2" title="Send now" onClick={() => sendNow(s.schedule_id)} data-testid={`send-now-${s.schedule_id}`}>
                            <Send className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => toggleActive(s)} title={s.is_active ? 'Pause' : 'Activate'}>
                            {s.is_active ? <Pause className="w-3.5 h-3.5" /> : <Play className="w-3.5 h-3.5" />}
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2" onClick={() => { setEditSched({ ...s }); setShowDialog(true); }}>
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                          <Button variant="ghost" size="sm" className="h-7 px-2 text-red-500" onClick={() => deleteSched(s.schedule_id)}>
                            <Trash2 className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </div>
        )}

        {/* History Tab */}
        {tab === 'history' && (
          <div className="space-y-2">
            {history.length === 0 ? (
              <Card className="border-gray-100">
                <CardContent className="py-12 text-center text-gray-400">
                  <History className="w-10 h-10 mx-auto mb-3 opacity-40" />
                  <p className="text-sm">No report history yet.</p>
                </CardContent>
              </Card>
            ) : (
              history.map(h => (
                <Card key={h.history_id} className="border-gray-100" data-testid={`history-row-${h.history_id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-center gap-3">
                      {h.status === 'sent' ? <CheckCircle className="w-5 h-5 text-emerald-500 flex-shrink-0" /> : <XCircle className="w-5 h-5 text-red-400 flex-shrink-0" />}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2">
                          <span className="text-sm font-medium text-[#0f1f33]">{h.report_name}</span>
                          <Badge className={`text-[9px] ${h.status === 'sent' ? 'bg-emerald-50 text-emerald-700' : 'bg-red-50 text-red-600'}`}>{h.status}</Badge>
                        </div>
                        <p className="text-xs text-gray-400">{h.recipient_emails?.join(', ')} — {h.filename}</p>
                      </div>
                      <span className="text-xs text-gray-400 flex-shrink-0">{new Date(h.sent_at).toLocaleString()}</span>
                    </div>
                  </CardContent>
                </Card>
              ))
            )}
          </div>
        )}

        {/* Schedule Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg">
            <DialogHeader><DialogTitle>{editSched?.schedule_id ? 'Edit Schedule' : 'New Scheduled Report'}</DialogTitle></DialogHeader>
            {editSched && (
              <div className="space-y-4">
                <div>
                  <label className="text-xs font-medium text-gray-500">Report Type</label>
                  <select value={editSched.report_type} onChange={e => setEditSched(p => ({ ...p, report_type: e.target.value }))} className="w-full h-9 rounded-md border px-2 text-sm" data-testid="report-type-select">
                    {reportTypes.map(t => <option key={t.id} value={t.id}>{t.name} — {t.description}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs font-medium text-gray-500">Frequency</label>
                  <select value={editSched.frequency} onChange={e => setEditSched(p => ({ ...p, frequency: e.target.value }))} className="w-full h-9 rounded-md border px-2 text-sm" data-testid="frequency-select">
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                </div>
                {editSched.frequency === 'weekly' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Day of Week</label>
                    <select value={editSched.day_of_week} onChange={e => setEditSched(p => ({ ...p, day_of_week: parseInt(e.target.value) }))} className="w-full h-9 rounded-md border px-2 text-sm">
                      {DAYS_OF_WEEK.map((d, i) => <option key={i} value={i}>{d}</option>)}
                    </select>
                  </div>
                )}
                {editSched.frequency === 'monthly' && (
                  <div>
                    <label className="text-xs font-medium text-gray-500">Day of Month</label>
                    <Input type="number" min="1" max="28" value={editSched.day_of_month} onChange={e => setEditSched(p => ({ ...p, day_of_month: parseInt(e.target.value) || 1 }))} />
                  </div>
                )}
                <div>
                  <label className="text-xs font-medium text-gray-500">Recipient Emails (one per line)</label>
                  <textarea
                    value={editSched.recipient_emails?.join('\n') || ''}
                    onChange={e => setEditSched(p => ({ ...p, recipient_emails: e.target.value.split('\n') }))}
                    rows={3}
                    className="w-full rounded-md border px-3 py-2 text-sm"
                    data-testid="recipient-emails-input"
                  />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDialog(false)}>Cancel</Button>
              <Button onClick={save} className="bg-[#1e3a5f] hover:bg-[#152d4a] text-white" data-testid="save-schedule-btn"><Save className="w-4 h-4 mr-1" /> Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
